///
/// EarthBrowser
///
/// Weather Alert Browser
///
/// Movable window for browsing active weather alerts from the local
/// synced dataset table
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { AlertDataProvider } from '@/data-providers/AlertDataProvider'
import { AlertsTable, type AlertProperties } from '@/earthbrowser/dataset-distribution/alerts/AlertsTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { TriangleAlert } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface AlertBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const AlertBrowserRenderer = memo(function AlertBrowserRenderer({
	windowId,
	windowState,
}: AlertBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const alertsVisible = visibleDatasets.includes("alert")

	// Get singleton scene manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [alerts, setAlerts] = useState<AlertProperties[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Load alerts from the local table (mount, sync refresh, manual refresh)
	const loadAlerts = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const loadedAlerts = await AlertsTable.getAll()
			setAlerts(loadedAlerts)
			setTimeUpdateTrigger(prev => prev + 1)

			const count = await AlertsTable.getCount()
			setTotalCount(count)
		} catch (error) {
			console.error('Failed to load alerts:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [])

	// Load alerts on mount
	useEffect(() => {
		void loadAlerts(true)
	}, [loadAlerts])

	// Reload silently whenever the alerts layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('alerts', () => {
		void loadAlerts(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		if (isLoading) return

		if (!isInitialized) {
			// Dispose old provider
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			// Create new provider with current data
			const provider = new AlertDataProvider({
				id: windowId,
				name: 'Weather Alerts',
				queryFn: async () => alerts
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			// Update the provider's cached data when alerts change (new sync etc.)
			const provider = DataProviderRegistry.get(windowId) as AlertDataProvider | undefined
			if (provider) {
				provider.updateCachedData(alerts)
			}
		}
	}, [alerts, windowId, isLoading, isInitialized])

	// Timer to update relative times every minute, aligned to the minute boundary
	useEffect(() => {
		let interval: NodeJS.Timeout | null = null

		const now = new Date()
		const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

		const timeout = setTimeout(() => {
			setTimeUpdateTrigger(prev => prev + 1)
			interval = setInterval(() => {
				setTimeUpdateTrigger(prev => prev + 1)
			}, 60000)
		}, msUntilNextMinute)

		return () => {
			clearTimeout(timeout)
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('alerts', alertsVisible)
		}
	}, [layerManager, alertsVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle alert row click
	const handleRowClick = useCallback((row: DataRow) => {
		const alert = row as unknown as AlertProperties
		const featureId = alert.id

		// Ensure alerts are visible on the map
		if (!alertsVisible) {
			dispatch(sendDatasetAction({
				type: "alert",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('alerts', true)
			}
		}

		// Focus the dataset-distribution scene object (AlertFeature)
		if (layerManager) {
			layerManager.focusSceneObject('alerts', featureId)
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, alertsVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadAlerts(true)
		if (layerManager) {
			void layerManager.refreshLayer('alerts')
		}
	}, [loadAlerts, layerManager])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		// Keep showing data during background refetches (don't flash loading indicator)
		if (provider && isInitialized) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`alert-browser-${alerts.length}-${timeUpdateTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search by event, area, severity..."
				/>
			)
		}

		// Loading state - only show when dataset is empty (initial load)
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-amber-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-amber-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Weather Alerts
					</p>
					<p className="text-sm text-white/60">
						Initializing...
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, handleRowClick, handleRefresh, totalCount, alerts.length, timeUpdateTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<TriangleAlert className="w-5 h-5" />}
			iconColor="text-amber-400"
			titlebarPrefix={<DatasetVisibilityToggle windowType="alert" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
